/**
 * SessionValidationGate
 *
 * Renders during bootstrap when the customer app has a recovery hint
 * (URL sessionUid, ev.session.v1 in localStorage, or the recovery
 * beacon) but the backend hasn't yet confirmed whether the session
 * is still valid. The goal is very narrow: **never let a stale local
 * snapshot render the charging Screen as if it were authoritative**.
 *
 * Two visual states, one component so the App.jsx wiring stays flat.
 *
 *   phase === 'validating'   → "Checking your charging session…"
 *   phase === 'reconnecting' → "Reconnecting…" banner + a short
 *                              recap of the last known session +
 *                              a manual "Start over" escape hatch.
 *
 * The reconnecting variant deliberately does NOT expose any Stop /
 * Cancel / Start controls — those all require backend confirmation
 * we don't yet have. The last-known summary is presented as
 * *information about the local snapshot*, not as a claim that the
 * charger is actively working right now.
 *
 * Kept as a thin presentational component so it can be rendered
 * from anywhere without touching SessionContext. All copy lives
 * here; there is no i18n system in place yet.
 */

import React from 'react';
import PropTypes from 'prop-types';
import './SessionValidationGate.less';
import { VALIDATION_PHASES } from '../../utils/sessionValidationPhase.js';

function formatKwh(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return `${value.toFixed(2)} kWh`;
}

function formatStarted(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export default function SessionValidationGate({ phase, lastKnown, onStartOver }) {
  if (phase === VALIDATION_PHASES.VALIDATING) {
    return (
      <div className="gc-validation-gate" role="status" aria-live="polite">
        <div className="gc-validation-gate-spinner" aria-hidden="true" />
        <h1 className="gc-validation-gate-title">Checking your charging session…</h1>
        <p className="gc-validation-gate-body">
          We're confirming the current state with the charger.
        </p>
      </div>
    );
  }

  if (phase === VALIDATION_PHASES.RECONNECTING) {
    // Best-effort context extracted from the local snapshot. Every
    // field is optional — we don't want to imply certainty about
    // any value while the backend is unreachable.
    const chargerLabel = lastKnown?.publicId || lastKnown?.stationId || null;
    const startedAt = formatStarted(lastKnown?.startedAt);
    const energyText = formatKwh(lastKnown?.energyKWh);

    return (
      <div className="gc-validation-gate gc-validation-gate-reconnecting" role="status" aria-live="polite">
        <div className="gc-validation-gate-spinner" aria-hidden="true" />
        <h1 className="gc-validation-gate-title">Reconnecting…</h1>
        <p className="gc-validation-gate-body">
          Showing the last known session while we reconnect. Charging
          controls are paused until we can verify the current state.
        </p>

        {(chargerLabel || startedAt || energyText) && (
          <dl className="gc-validation-gate-lastknown" aria-label="Last known session">
            {chargerLabel && (
              <>
                <dt>Charger</dt>
                <dd>{chargerLabel}</dd>
              </>
            )}
            {startedAt && (
              <>
                <dt>Started</dt>
                <dd>{startedAt}</dd>
              </>
            )}
            {energyText && (
              <>
                <dt>Last reading</dt>
                <dd>{energyText}</dd>
              </>
            )}
          </dl>
        )}

        {typeof onStartOver === 'function' && (
          <button
            type="button"
            className="gc-validation-gate-startover"
            onClick={onStartOver}
          >
            Start over
          </button>
        )}
      </div>
    );
  }

  // idle / ready — the gate has nothing to render; the caller should
  // not have mounted us at all. Return null defensively.
  return null;
}

SessionValidationGate.propTypes = {
  phase: PropTypes.oneOf(Object.values(VALIDATION_PHASES)).isRequired,
  lastKnown: PropTypes.shape({
    sessionUid: PropTypes.string,
    stationId: PropTypes.string,
    publicId: PropTypes.string,
    startedAt: PropTypes.string,
    energyKWh: PropTypes.number,
  }),
  onStartOver: PropTypes.func,
};
